import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCampaignPacing,
  setPacingKill,
  updateCampaignPacing,
  type CampaignPacing,
  type PacingAction,
  type PacingEvent,
} from "../api/pacing";
import { fmtDateTimeInZone, fmtTimeInZone } from "../lib/time";

// Automatic dial pacing: settings, the current cap, the trailing abandon
// rates the FCC measures, and the written reason behind every move. All
// instants render in the API's `status.timezone` (the one system zone).

const ACTION_BADGE: Record<PacingAction, string> = {
  lower: "bg-amber-100 text-amber-800",
  raise: "bg-green-100 text-green-800",
  clamp: "bg-red-100 text-red-800",
  hold: "bg-gray-200 text-gray-600",
  external: "bg-blue-100 text-blue-800",
  enabled: "bg-blue-100 text-blue-800",
  disabled: "bg-gray-200 text-gray-600",
  settings: "bg-blue-100 text-blue-800",
};

function pct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined) return "—";
  return `${v.toFixed(digits)}%`;
}

function capChange(e: PacingEvent): string {
  if (e.old_cap === null || e.new_cap === null) return "—";
  if (e.old_cap === e.new_cap) return String(e.new_cap);
  return `${e.old_cap} → ${e.new_cap}`;
}

function sampleText(e: PacingEvent): string {
  if (e.connected === null || e.abandoned === null) return "—";
  const resolved = e.connected + e.abandoned;
  const waiting = e.pending ? ` · ${e.pending} waiting` : "";
  return `${e.abandoned}/${resolved}${waiting}`;
}

function windowText(e: PacingEvent, tz: string): string {
  if (!e.window_start || !e.window_end) return "—";
  return `${fmtTimeInZone(e.window_start, tz)} – ${fmtTimeInZone(e.window_end, tz)}`;
}

function actorText(actor: string): string {
  if (actor === "controller") return "pacing";
  if (actor.startsWith("usr:")) return `operator #${actor.slice(4)}`;
  return actor;
}

function StatTile({
  label,
  value,
  sub,
  over,
}: {
  label: string;
  value: string;
  sub: string;
  over: boolean;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-semibold ${over ? "text-red-700" : "text-gray-900"}`}>{value}</div>
      <div className="text-[11px] text-gray-400">{sub}</div>
    </div>
  );
}

export function DialPacingPanel({ campaignId, name }: { campaignId: number; name: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["campaign-pacing", campaignId],
    queryFn: () => getCampaignPacing(campaignId),
    refetchInterval: 15000,
  });

  const [enabled, setEnabled] = useState(false);
  const [target, setTarget] = useState("3");
  const [minCh, setMinCh] = useState("");
  const [maxCh, setMaxCh] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Pre-fill from the server until the operator starts editing; a background
  // refetch must not overwrite half-typed numbers.
  useEffect(() => {
    if (!data || dirty) return;
    setEnabled(data.settings.enabled);
    setTarget(String(data.settings.target_pct));
    setMinCh(String(data.settings.min_channels));
    setMaxCh(String(data.settings.max_channels));
  }, [data, dirty]);

  const onSaved = (fresh: CampaignPacing) => {
    setDirty(false);
    setSaveError(null);
    qc.setQueryData(["campaign-pacing", campaignId], fresh);
    qc.invalidateQueries({ queryKey: ["campaigns"] });
  };
  const save = useMutation({
    mutationFn: () =>
      updateCampaignPacing(campaignId, {
        enabled,
        target_pct: Number(target),
        min_channels: Number(minCh),
        max_channels: Number(maxCh),
      }),
    onSuccess: onSaved,
    onError: (err: unknown) => setSaveError((err as Error).message || "Save failed"),
  });
  const kill = useMutation({
    mutationFn: (killed: boolean) => setPacingKill(killed),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaign-pacing"] }),
  });

  if (isLoading) return <p className="mt-4 text-gray-500">Loading pacing…</p>;
  if (isError || !data) {
    return <p className="mt-4 text-red-600">Failed to load pacing: {(error as Error)?.message}</p>;
  }

  const { status, settings, latest, events, trailing } = data;
  const tz = status.timezone;
  const targetNum = Number(target) || settings.target_pct;
  const notAgentQueue = settings.transfer_target !== "agents";
  const capAboveMax = settings.enabled && settings.current_channel > settings.max_channels;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-semibold">
          Dial pacing — <span className="text-brand">{name}</span>
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`px-2 py-0.5 rounded ${status.armed ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}
          >
            {status.armed ? "Pacing armed" : "Pacing not armed"}
          </span>
          <span
            className={`px-2 py-0.5 rounded ${status.killed ? "bg-red-100 text-red-800" : "bg-gray-200 text-gray-600"}`}
          >
            {status.killed ? "Kill switch ON" : "Kill switch off"}
          </span>
          {status.can_kill && (
            <button
              onClick={() => {
                if (status.killed || confirm("Stop every automatic cap adjustment now? Caps stay where they are.")) {
                  kill.mutate(!status.killed);
                }
              }}
              className={`px-2 py-1 rounded text-white ${status.killed ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
            >
              {status.killed ? "Resume pacing" : "Kill all pacing"}
            </button>
          )}
        </div>
      </div>

      {!status.armed && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Automatic pacing is not armed on this system. Settings are saved, but no channel cap will move until it is.
        </div>
      )}
      {status.killed && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800">
          The global kill switch is on: no campaign's cap is being adjusted. Caps stay where they were.
        </div>
      )}
      {notAgentQueue && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          This campaign hands press-1 callers to an outside number, not to your agent queue, so there are no
          queue outcomes to pace on. Pacing will hold and never move the cap.
        </div>
      )}
      {capAboveMax && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          The current cap ({settings.current_channel}) is above the pacing maximum ({settings.max_channels});
          the next evaluation will bring it down to the maximum.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4 lg:col-span-1">
          <h3 className="text-sm font-semibold mb-2">Settings</h3>
          <p className="text-xs text-gray-500 mb-3">
            Moves this campaign's channel cap between the minimum and maximum to hold the abandon rate near
            the target. Small steps (at most {status.max_step_down_pct}% down, {status.max_step_up_pct}% up),
            never before {status.min_sample} callers have entered the queue under the current cap. Turning it
            off leaves the cap where it is.
          </p>
          <label htmlFor="pacing-enabled" className="flex items-center gap-2 text-sm mb-3">
            <input
              id="pacing-enabled"
              type="checkbox"
              checked={enabled}
              onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }}
            />
            Pace this campaign automatically
          </label>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <label htmlFor="pacing-target" className="block text-xs text-gray-500">Target abandon %</label>
              <input
                id="pacing-target"
                type="number"
                step="0.1"
                min="0.5"
                max="25"
                value={target}
                onChange={(e) => { setTarget(e.target.value); setDirty(true); }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label htmlFor="pacing-min" className="block text-xs text-gray-500">Min channels</label>
              <input
                id="pacing-min"
                type="number"
                min="1"
                value={minCh}
                onChange={(e) => { setMinCh(e.target.value); setDirty(true); }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label htmlFor="pacing-max" className="block text-xs text-gray-500">Max channels</label>
              <input
                id="pacing-max"
                type="number"
                min="1"
                value={maxCh}
                onChange={(e) => { setMaxCh(e.target.value); setDirty(true); }}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Current cap: <b className="text-gray-800">{settings.current_channel}</b> · campaign{" "}
            <b className="text-gray-800">{settings.campaign_status}</b>
          </div>
          {saveError && <p role="alert" className="mt-2 text-xs text-red-600">{saveError}</p>}
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !dirty}
            className="mt-3 w-full bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-40"
          >
            {save.isPending ? "Saving…" : "Save pacing settings"}
          </button>
        </div>

        <div className="lg:col-span-2">
          <div className="grid grid-cols-3 gap-3 mb-3">
            {trailing.map((t) => {
              const resolved = t.connected + t.abandoned;
              return (
                <StatTile
                  key={t.label}
                  label={`Abandon rate · last ${t.label}`}
                  value={pct(t.pct)}
                  sub={resolved ? `${t.abandoned} of ${resolved} queue callers` : "no queue callers"}
                  over={t.pct !== null && t.pct > targetNum}
                />
              );
            })}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold mb-1">Latest evaluation</h3>
            {latest ? (
              <p className="text-sm text-gray-700">
                <span className={`px-2 py-0.5 rounded text-xs mr-2 ${ACTION_BADGE[latest.action]}`}>
                  {latest.action}
                </span>
                <span className="text-gray-500 text-xs mr-2">{fmtDateTimeInZone(latest.created_at, tz)}</span>
                {latest.reason}
              </p>
            ) : (
              <p className="text-sm text-gray-400">No evaluations yet.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden mt-4">
        <div className="px-4 py-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">History ({tz})</h3>
          <span className="text-[11px] text-gray-400">
            Every cap change, plus holds when the reason changes or every {Math.round(300 / 60)} minutes
          </span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">When</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2 text-right">Cap</th>
              <th className="px-4 py-2 text-right">Abandon</th>
              <th className="px-4 py-2 text-right">Sample</th>
              <th className="px-4 py-2">Window</th>
              <th className="px-4 py-2">Reason</th>
              <th className="px-4 py-2">By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {events.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-3 text-gray-500">No pacing history for this campaign yet.</td></tr>
            ) : (
              events.map((e) => (
                <tr key={e.dial_pacing_event_id}>
                  <td className="px-4 py-2 whitespace-nowrap">{fmtDateTimeInZone(e.created_at, tz)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${ACTION_BADGE[e.action]}`}>{e.action}</span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono whitespace-nowrap">{capChange(e)}</td>
                  <td className="px-4 py-2 text-right font-mono">{pct(e.observed_pct)}</td>
                  <td className="px-4 py-2 text-right font-mono whitespace-nowrap">{sampleText(e)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">{windowText(e, tz)}</td>
                  <td className="px-4 py-2 text-gray-700">{e.reason}</td>
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{actorText(e.actor)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
